import { yupResolver } from "@hookform/resolvers/yup";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, LanguageSelector } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { IMAGE_URL } from "config";
import {
  employeeSchema,
  useCreatePublicEmployee,
  useEmployeesStore
} from "features/hr";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { EmployeeModalSteps } from "../../hr/components/EmployeeModalSteps";
import { BankForm } from "../../hr/components/Forms/BankForm";
import { EducationBackgroundForm } from "../../hr/components/Forms/EducationBackgroundForm";
import { EmergencyContactForm } from "../../hr/components/Forms/EmergencyContactForm";
import { EmployerInfoForm } from "../../hr/components/Forms/EmployerInfoForm";
import { FamilyInfoForm } from "../../hr/components/Forms/FamilyInfoForm";
import { JobForm } from "../../hr/components/Forms/JobForm";
import { PersonalInfoForm } from "../../hr/components/Forms/PersonalInfoForm";
import { useDenomination } from "../api";
import { SuccessModal } from "../components/reporting-form/SuccessModal";
import { useDenominationStore } from "../store";

const StyledHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(to right, #00B0B9, #008087, #00B0B9)`,
  color: "white",
  padding: "20px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: "12px 8px",
    gap: "12px"
  }
}));

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: theme.shadows[0],
  padding: "24px"
}));

export const DenominationEmployeeRegistration = () => {
  const { denominationId, formToken } = useParams();
  const navigate = useNavigate();
  // Copy exact state management from AddEmployeeModal
  const addEmployeeFormActiveStep = useEmployeesStore(
    state => state.addEmployeeFormActiveStep
  );
  const setAddEmployeeFormActiveStep = useEmployeesStore(
    state => state.setAddEmployeeFormActiveStep
  );

  const { t } = useTranslation();
  const createEmployeeMutation = useCreatePublicEmployee();
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );

  const {
    data: denomination,
    isLoading: isDenominationLoading,
    isError: isDenominationError
  } = useDenomination({
    denominationId,
    isPublic: true,
    config: {
      enabled: !!denominationId
    }
  });

  // Copy exact form setup from AddEmployeeModal
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger
  } = useForm({
    defaultValues: {
      employee_id: "",
      title: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      gender: "",
      date_of_birth: new Date(),
      nationality_id: "66",
      marital_status: "",
      employee_photo: null,
      phone_number: "",
      email: "",
      address: "",
      position: "",
      employment_date: new Date(),
      employment_type: "",
      salary_bank_name: "",
      salary_bank_account_number: "",
      pf_bank_name: "",
      pf_bank_account_number: "",
      social_id_number: "",
      tin_number: "",
      gross_salary: "",
      overtime: "",
      transport_allowance: "",
      house_allowance: "",
      communication_allowance: "",
      bonus: "",
      tax: "",
      credit: "",
      pension: "",
      provident_fund: "",
      penalty: "",
      emergency_contact_title: "",
      emergency_contact_first_name: "",
      emergency_contact_last_name: "",
      emergency_contact_middle_name: "",
      relationship_to_employee: "",
      emergency_contact_phone: "",
      emergency_contact_address: "",
      has_children: false,
      children: [],
      education_background: [],
      has_experience: false,
      work_experience: [],
      employer_name: "",
      employer_location: "",
      employer_house_number: "",
      employer_phone_number: "",
      mother_name: "",
      date_married: "",
      spouse_name: "",
      spouse_phone_number: "",
      spouse_job_title: "",
      birth_country_id: "",
      birth_region_id: "",
      birth_zone_id: "",
      birth_city_id: "",
      birth_sub_city_id: "",
      birth_woreda_id: "",
      employee_doc: null
    },
    mode: "onBlur",
    resolver: yupResolver(employeeSchema)
  });

  useEffect(() => {
    if (denomination?.result) {
      setValue("employer_name", denomination?.result?.name);
    }
  }, [denomination?.result]);

  // Copy exact step fields definition from AddEmployeeModal
  const stepFields = {
    1: [
      "employer_name",
      "employer_location",
      "employer_house_number",
      "employer_phone_number"
    ],
    2: [
      "employee_id",
      "title",
      "first_name",
      "middle_name",
      "last_name",
      "gender",
      "date_of_birth",
      "nationality_id",
      "marital_status",
      "employee_photo",
      "phone_number",
      "email",
      "address",
      "mother_name",
      "birth_country_id",
      "birth_region_id",
      "birth_zone_id",
      "birth_city_id",
      "birth_sub_city_id",
      "birth_woreda_id"
    ],
    3: [
      "marital_status",
      "date_married",
      "spouse_name",
      "spouse_phone_number",
      "spouse_job_title",
      "has_children",
      "children"
    ],
    4: [
      "education_background",
      "has_experience",
      "work_experience",
      "employee_doc"
    ],
    5: [
      "position",
      "employment_date",
      "employment_type",
      "has_experience",
      "work_experience"
    ],
    6: [
      "salary_bank_name",
      "salary_bank_account_number",
      "pf_bank_name",
      "pf_bank_account_number",
      "social_id_number",
      "tin_number",
      "gross_salary",
      "overtime",
      "transport_allowance",
      "house_allowance",
      "communication_allowance",
      "bonus",
      "tax",
      "credit",
      "pension",
      "provident_fund",
      "penalty"
    ],
    7: [
      "emergency_contact_title",
      "emergency_contact_first_name",
      "emergency_contact_middle_name",
      "emergency_contact_last_name",
      "relationship_to_employee",
      "emergency_contact_phone",
      "emergency_contact_address"
    ]
  };

  // Copy exact functions from AddEmployeeModal
  const findStepWithErrors = () => {
    if (!errors || Object.keys(errors).length === 0) return null;

    for (const [step, fields] of Object.entries(stepFields)) {
      const hasError = fields.some(field => errors[field]);
      if (hasError) {
        return parseInt(step);
      }
    }
    return null;
  };

  const handleNext = async () => {
    // Validate current step fields
    const currentStepFields = stepFields[addEmployeeFormActiveStep];
    const isValid = await trigger(currentStepFields);

    if (isValid) {
      setAddEmployeeFormActiveStep(addEmployeeFormActiveStep + 1);
    }
  };

  // Copy exact forms definition from AddEmployeeModal
  const forms = {
    1: (
      <EmployerInfoForm
        register={register}
        errors={errors}
        control={control}
        denominationId={
          window.location.pathname.includes("denomination")
            ? selectedDenomination?.id
            : undefined
        }
        watch={watch}
        setValue={setValue}
      />
    ),
    2: (
      <PersonalInfoForm
        register={register}
        errors={errors}
        control={control}
        setValue={setValue}
        watch={watch}
      />
    ),
    3: (
      <FamilyInfoForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    4: (
      <EducationBackgroundForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    5: (
      <JobForm
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
      />
    ),
    6: <BankForm register={register} errors={errors} watch={watch} />,
    7: (
      <EmergencyContactForm
        register={register}
        errors={errors}
        control={control}
      />
    )
  };

  // Copy exact onSubmit function from AddEmployeeModal
  const onSubmit = values => {
    const formData = new FormData();

    // Helper function to format date to ISO format (YYYY-MM-DD)
    const formatDate = date => {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD");
    };

    // Filter out null/undefined values and convert to FormData
    Object.entries(values)
      .filter(([, value]) => value != null)
      .forEach(([key, value]) => {
        if ((key === "employee_photo" || key === "employee_doc") && value) {
          // Only append file if it has files
          if (value instanceof FileList && value.length > 0) {
            formData.append(key, value[0]);
          } else if (value instanceof File) {
            formData.append(key, value);
          }
        } else if (key === "has_children" || key === "has_experience") {
          formData.append(key, value ? "1" : "0");
        } else if (Array.isArray(value)) {
          // Handle arrays by appending each item with array notation
          value.forEach((item, index) => {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              if (itemValue != null && itemValue !== "") {
                formData.append(`${key}[${index}][${itemKey}]`, itemValue);
              }
            });
          });
        } else if (value instanceof Date) {
          formData.append(key, formatDate(value));
        } else if (value !== "") {
          formData.append(key, value);
        }
      });

    createEmployeeMutation.mutate(
      {
        employeeData: formData,
        denominationId: formToken
      },
      {
        onSuccess: () => {
          setOpenSuccessModal(true);
          reset();
          setAddEmployeeFormActiveStep(1);
        }
      }
    );
  };

  // Copy exact handleFormSubmit from AddEmployeeModal
  const handleFormSubmit = handleSubmit(data => {
    const stepWithErrors = findStepWithErrors();
    if (stepWithErrors) {
      // Navigate to the step with errors
      setAddEmployeeFormActiveStep(stepWithErrors);
    } else {
      // If no errors, proceed with form submission
      onSubmit(data);
    }
  });

  const handleCancel = () => {
    reset();
    setAddEmployeeFormActiveStep(1);
  };

  if (isDenominationLoading) {
    return <LoadingScreen />;
  }

  if (
    isDenominationError ||
    !denomination?.result ||
    !denomination?.result?.form_token ||
    denomination?.result?.form_token !== formToken
  ) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh">
        <Stack px={2} maxWidth={900} spacing={2}>
          <Typography variant="h4" fontWeight={700} textAlign="center">
            {t("common.brokenLink")}
          </Typography>
          <Typography textAlign="center">
            {t("common.brokenLinkDescription")}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: "100vh", overflowY: "auto" }}>
      {/* Header with denomination information */}
      <StyledHeader>
        <Avatar
          src={`${IMAGE_URL}/${denomination?.result?.image_url}`}
          alt={denomination?.result?.name}
          sx={{
            border: "2px solid",
            borderColor: "primary.main",
            width: { xs: 70, sm: 100 },
            height: { xs: 70, sm: 100 },
            mb: { xs: 1, sm: 0 }
          }}
        />
        <Stack alignItems="center">
          <Typography variant="h5" align="center" gutterBottom>
            {denomination?.result?.name}
          </Typography>
          <Typography variant="subtitle1" align="center">
            የአገልግሎት ሪፖርት መሙያ ፎርም
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="center"
            spacing={{ xs: 1, sm: 3 }}
            mt={1}
            sx={{ alignItems: { xs: "center", sm: "center" } }}
          >
            <Typography>
              ፖስታ ሳጥን ቁጥር: {denomination?.result?.p_o_box}
            </Typography>
            <Typography>ኢሜል: {denomination?.result?.email}</Typography>
            <Typography>ስልክ: {denomination?.result?.phone}</Typography>
          </Stack>
        </Stack>
      </StyledHeader>

      <Stack
        sx={{
          padding: "20px",
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto"
        }}
      >
        <FormContainer>
          {/* Form Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h6"
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              {t("HR.AddEmployee.EmployeeRegistration")}
            </Typography>
          </Stack>

          <Stack py={1} spacing={2}>
            <EmployeeModalSteps />
            {forms[addEmployeeFormActiveStep]}
            <MutationError
              error={createEmployeeMutation.error}
              sx={{ mt: 2 }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              flavor="text"
              onClick={handleCancel}
              disabled={createEmployeeMutation.isLoading}
            >
              {t("form.common.cancel")}
            </Button>
            {addEmployeeFormActiveStep !== 1 && (
              <Button
                flavor="outlined"
                onClick={() =>
                  setAddEmployeeFormActiveStep(addEmployeeFormActiveStep - 1)
                }
                disabled={createEmployeeMutation.isLoading}
              >
                {t("form.common.back")}
              </Button>
            )}
            {addEmployeeFormActiveStep !== 7 && (
              <Button onClick={handleNext}>{t("form.common.next")}</Button>
            )}
            {addEmployeeFormActiveStep === 7 && (
              <Button
                onClick={handleFormSubmit}
                disabled={createEmployeeMutation.isLoading}
              >
                {t("form.common.submit")}
              </Button>
            )}
          </Stack>
        </FormContainer>
      </Stack>

      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 24,
          "& select": {
            color: "white"
          }
        }}
      >
        <LanguageSelector />
      </Box>

      <SuccessModal
        open={openSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
          navigate(-1);
        }}
        title={t("successModal.title")}
        message={t("successModal.message")}
        buttonText={t("successModal.buttonText")}
      />
    </Stack>
  );
};
